import { ZegoUIKitPrebuilt } from "@zegocloud/zego-uikit-prebuilt";
import { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Button, Card, Modal, Table, Badge, Spinner } from "react-bootstrap";
import { FaFileAlt } from "react-icons/fa";
import { getURLbyEndPointV2 } from "../../../../../store/api";
import axios from "axios";

const Room = () => {
  const { roomId } = useParams();
  const navigate = useNavigate();
  const meetingContainer = useRef(null);
  const [disputeDetails, setDisputeDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showDocumentModal, setShowDocumentModal] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [isSidebarOpen, setIsSidebarOpen] = useState(true); // Sidebar state

  const urlParams = new URLSearchParams(window.location.search);
  const username = urlParams.get("username") || "AECCI Administrator";

  // Fetch dispute details by roomId
  const fetchDisputeDetails = async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        getURLbyEndPointV2("getDisputeByMeetingRoomId") + roomId
      );

      if (response.data.status && response.data.data) {
        setDisputeDetails(response.data.data);
      } else {
        setDisputeDetails(null);
      }
    } catch (error) {
      console.error("Error fetching dispute details:", error);
      setDisputeDetails(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDisputeDetails();
  }, [roomId]);

  // Initialize Zego Meeting
  const myMeeting = async (element) => {
    //ed maa;am account id
    const appID = 1871154113;
    const serverSecret = "7625a483cfcaa01920c9f3ceeaab8ff2";

    const kitToken = ZegoUIKitPrebuilt.generateKitTokenForTest(
      appID,
      serverSecret,
      roomId,
      Date.now().toString(),
      username
    );

    const zp = ZegoUIKitPrebuilt.create(kitToken);

    // joinRoom accepts callbacks like onLeaveRoom, onError
    zp.joinRoom({
      container: element,
      maxUsers: 10,
      scenario: { mode: ZegoUIKitPrebuilt.GroupCall },
      showPreJoinView: true,
      preJoinViewConfig: { title: "AECCI Arbitration Hearing" },
      branding: {
        headerTitle: "AECCIVirtual Arbitration Hearing",
        showHeader: true,
      },
      layout: "Grid",
      showRoomDetailsButton: true,
      showRoomTimer: true,
      showMoreButton: true,
      showMyCameraToggleButton: true,
      showMyMicrophoneToggleButton: true,
      showAudioVideoSettingsButton: true,
      showTurnOffRemoteCameraButton: true,
      showTurnOffRemoteMicrophoneButton: true,
      showScreenSharingButton: true, // Ensure screen sharing is enabled
      turnOnMicrophoneWhenJoining: false,
      turnOnCameraWhenJoining: true,
      useFrontFacingCamera: true,
      enableStereo: false,
      sharedLinks: [
        {
          name: "Join via link",
          url: window.location.href,
        },
      ],
      leaveConfirmDialogInfo: {
        title: "Leave Meeting",
        message: "Are you sure you want to leave the meeting?",
        cancelButtonName: "Cancel",
        confirmButtonName: "Yes, leave",
      },
      // important: use provided callbacks rather than .on()
      onLeaveRoom: () => {
        console.log("Meeting closed → redirecting...");
        navigate("/raise-your-dispute");
      },
      onError: (err) => {
        console.warn("Zego error:", err);
      },
    });
  };

  useEffect(() => {
    if (!meetingContainer.current) return;
    const timeout = setTimeout(() => {
      try {
        myMeeting(meetingContainer.current);
      } catch (error) {
        console.error("Failed to start Zego meeting:", error);
      }
    }, 500);
    return () => clearTimeout(timeout);
  }, [roomId, username]);

  // Helper functions
  const renderValue = (v) => (v || v === 0 ? v : "N/A");

  const formatDate = (d) => {
    if (!d) return "N/A";
    try {
      return new Date(d).toLocaleDateString("en-US", {
        year: "numeric",
        month: "long",
        day: "numeric",
      });
    } catch {
      return "N/A";
    }
  };

  const renderCurrency = (a) =>
    a || a === 0
      ? new Intl.NumberFormat("en-US", {
          style: "currency",
          currency: "USD",
        }).format(a)
      : "N/A";

  const getStatusVariant = (s) => {
    switch (String(s || "").toLowerCase()) {
      case "pending":
        return "warning";
      case "approved":
        return "success";
      case "rejected":
        return "danger";
      case "document verified":
        return "info";
      default:
        return "secondary";
    }
  };

  const getDocuments = (data) => {
    if (!data) return [];

    const docKeys = [
      { key: "companyRegistrationDoc", label: "Company Registration Document" },
      { key: "agreementContract", label: "Agreement Contract" },
      { key: "arbitrationForm", label: "Arbitration Form" },
      {
        key: "correspondenceWithSupplier",
        label: "Correspondence with Supplier",
      },
      { key: "commercialInvoice", label: "Commercial Invoice" },
      { key: "inspectionReports", label: "Inspection Reports" },
      { key: "correspondenceWithAgent", label: "Correspondence with Agent" },
      {
        key: "correspondenceWithCarrier",
        label: "Correspondence with Carrier",
      },
      { key: "photographs", label: "Photographs" },
      { key: "legalActionDocuments", label: "Legal Action Documents" },
    ];

    // Single-field documents from the dispute data
    const singleFieldDocs = docKeys
      .filter((doc) => data[doc.key])
      .map((doc) => ({
        documentType: doc.label,
        uploadDate: data.uploadDate || data.createdAt || new Date(),
        status: "Uploaded",
        url: data[doc.key],
        source: "field",
      }));

    // Documents array from dispute data (if any)
    const arrayDocs = Array.isArray(data.documents)
      ? data.documents
          .filter((d) => d && d.url)
          .map((d) => ({
            documentType: d.documentType || d.label || "Document",
            uploadDate: d.uploadDate || d.createdAt || new Date(),
            status: d.status || "Uploaded",
            url: d.url,
            source: "documents_array",
          }))
      : [];

    // Additional documents from clientId and paymentSchedule
    const additionalDocs = [];

    // Membership Certificate from clientId
    if (data.clientId?.membershipCertificate) {
      additionalDocs.push({
        documentType: "Membership Certificate",
        uploadDate: data.createdAt || new Date(),
        status: "Uploaded",
        url: data.clientId.membershipCertificate,
        source: "clientId",
      });
    }

    // Payment Receipt from paymentSchedule
    if (data.paymentSchedule?.paymentDetails?.receipt) {
      additionalDocs.push({
        documentType: "Payment Receipt",
        uploadDate:
          data.paymentSchedule.paymentDetails.paymentCompleteDate || new Date(),
        status: "Uploaded",
        url: data.paymentSchedule.paymentDetails.receipt,
        source: "paymentSchedule",
      });
    }

    // Merge all documents
    const merged = [...singleFieldDocs, ...arrayDocs, ...additionalDocs];

    // Deduplicate by URL to avoid duplicates
    const seen = new Set();
    const unique = merged.filter((doc) => {
      if (!doc.url) return false;
      if (seen.has(doc.url)) return false;
      seen.add(doc.url);
      return true;
    });

    return unique;
  };

  const getTimeTableDocuments = (data) => {
    if (!data?.timeTable?.sections) return [];
    return data.timeTable.sections.flatMap((section) =>
      Object.entries(section.documents || {}).map(([docKey, url]) => ({
        documentType: `${section.sectionName} - ${docKey}`,
        uploadDate: section.completedAt || section.createdAt || data.createdAt,
        status: section.isCompleted ? "Uploaded" : "Pending",
        url,
      }))
    );
  };

  // Handle document view
  const handleViewDocument = (doc) => {
    setSelectedDocument(doc);
    setShowDocumentModal(true);
  };

  const handleCloseDocumentModal = () => {
    setShowDocumentModal(false);
    setSelectedDocument(null);
  };

  // Toggle sidebar
  const toggleSidebar = () => {
    setIsSidebarOpen(!isSidebarOpen);
  };

  // Join Requests (Waiting Room) Logic
  const [showWaitingRoomModal, setShowWaitingRoomModal] = useState(false);
  const [pendingRequests, setPendingRequests] = useState([]);
  const [reqLoading, setReqLoading] = useState(false);

  const fetchJoinRequests = async () => {
    try {
      setReqLoading(true);
      const res = await axios.get(
        getURLbyEndPointV2("getJoinRequests") + roomId
      );
      if (res.data.status) {
        setPendingRequests(res.data.requests);
      }
    } catch (error) {
      console.error("Fetch requests failed:", error);
    } finally {
      setReqLoading(false);
    }
  };

  const approveRequest = async (userId) => {
    try {
      await axios.post(getURLbyEndPointV2("approveJoinRequest"), {
        meetingRoomId: roomId,
        userId,
      });
      fetchJoinRequests();
    } catch (e) {
      alert("Failed to approve");
    }
  };

  const rejectRequest = async (userId) => {
    try {
      await axios.post(getURLbyEndPointV2("rejectJoinRequest"), {
        meetingRoomId: roomId,
        userId,
      });
      fetchJoinRequests();
    } catch (e) {
      alert("Failed to reject");
    }
  };

  // Poll requests when modal is open
  useEffect(() => {
    let interval;
    if (showWaitingRoomModal) {
      fetchJoinRequests();
      interval = setInterval(fetchJoinRequests, 3000);
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [showWaitingRoomModal, roomId]);

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        width: "100vw",
        height: "100vh",
        backgroundColor: "#0B1A33",
        color: "white",
      }}
    >
      {/* HEADER */}
      <div
        style={{
          height: "60px",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          backgroundColor: "#0D274A",
          borderBottom: "2px solid #1E3A5F",
          fontSize: "20px",
          fontWeight: "600",
          textTransform: "uppercase",
          letterSpacing: "1px",
          position: "relative",
          padding: "0 20px",
          color: "#fff",
        }}
      >
        {/* LEFT: Logo + Center Name */}
        <div
          style={{
            position: "absolute",
            left: 20,
            display: "flex",
            alignItems: "center",
            gap: "10px",
            whiteSpace: "nowrap",
          }}
        >
          <img
            src="https://www.aecci.org.in/wp-content/uploads/2020/10/AECCI-24.png"
            alt="AECCI Logo"
            style={{ height: "38px" }}
          />
          <span style={{ fontSize: "16px", fontWeight: 600 }}>
            INTERNATIONAL ARBITRATION CENTER
          </span>
        </div>

        {/* CENTER TITLE */}
        <div
          style={{
            position: "absolute",
            left: "50%",
            transform: "translateX(-50%)",
            whiteSpace: "nowrap",
          }}
        >
          AECCI-IAC VIRTUAL ARBITRATION HEARING
        </div>

        {/* RIGHT: Case ID & Recording Control */}
        <div
          style={{
            position: "absolute",
            right: 20,
            display: "flex", // Use Flexbox
            alignItems: "center",
            gap: "15px",
            fontSize: "15px",
            fontWeight: "600",
            whiteSpace: "nowrap",
          }}
        >
          <Button
            variant="warning"
            size="sm"
            onClick={() => setShowWaitingRoomModal(true)}
            className="position-relative"
          >
            Waiting Room
            {pendingRequests.length > 0 && (
              <Badge
                bg="danger"
                pill
                className="position-absolute top-0 start-100 translate-middle"
              >
                {pendingRequests.length}
              </Badge>
            )}
          </Button>

          {/* Recording Controls */}
          {(() => {
            const status = disputeDetails?.currentMeeting?.recordingStatus;

            if (status === "RECORDING") {
              return (
                <>
                  <Button
                    variant="warning"
                    size="sm"
                    onClick={async () => {
                      try {
                        await axios.post(getURLbyEndPointV2("pauseRecording"), {
                          meetingRoomId: roomId,
                        });
                        alert("Recording Paused");
                        fetchDisputeDetails();
                      } catch (e) {
                        alert("Failed to pause");
                      }
                    }}
                  >
                    Pause
                  </Button>

                  <Button
                    variant="info"
                    size="sm"
                    onClick={async () => {
                      try {
                        const res = await axios.post(
                          getURLbyEndPointV2("takeSnapshot"),
                          { meetingRoomId: roomId }
                        );
                        alert("Snapshot taken");
                        console.log(res.data);
                      } catch (e) {
                        alert("Failed to take snapshot");
                      }
                    }}
                  >
                    Snapshot
                  </Button>

                  <Button
                    variant="danger"
                    size="sm"
                    onClick={async () => {
                      if (!window.confirm("Stop Cloud Recording?")) return;
                      try {
                        await axios.post(getURLbyEndPointV2("stopRecording"), {
                          meetingRoomId: roomId,
                        });
                        alert("Recording Stopped");
                        fetchDisputeDetails();
                      } catch (e) {
                        alert("Failed to stop");
                      }
                    }}
                  >
                    Stop Rec
                  </Button>
                </>
              );
            } else if (status === "PAUSED") {
              return (
                <>
                  <Button
                    variant="success"
                    size="sm"
                    onClick={async () => {
                      try {
                        await axios.post(
                          getURLbyEndPointV2("resumeRecording"),
                          { meetingRoomId: roomId }
                        );
                        alert("Recording Resumed");
                        fetchDisputeDetails();
                      } catch (e) {
                        alert("Failed to resume");
                      }
                    }}
                  >
                    Resume
                  </Button>

                  <Button
                    variant="info"
                    size="sm"
                    onClick={async () => {
                      try {
                        await axios.post(getURLbyEndPointV2("takeSnapshot"), {
                          meetingRoomId: roomId,
                        });
                        alert("Snapshot taken");
                      } catch (e) {
                        alert("Failed to take snapshot");
                      }
                    }}
                  >
                    Snapshot
                  </Button>

                  <Button
                    variant="danger"
                    size="sm"
                    onClick={async () => {
                      if (!window.confirm("Stop Cloud Recording?")) return;
                      try {
                        await axios.post(getURLbyEndPointV2("stopRecording"), {
                          meetingRoomId: roomId,
                        });
                        alert("Recording Stopped");
                        fetchDisputeDetails();
                      } catch (e) {
                        alert("Failed to stop");
                      }
                    }}
                  >
                    Stop Rec
                  </Button>
                </>
              );
            } else {
              return (
                <Button
                  variant="success"
                  size="sm"
                  onClick={async () => {
                    if (!window.confirm("Start Cloud Recording?")) return;
                    try {
                      await axios.post(getURLbyEndPointV2("startRecording"), {
                        meetingRoomId: roomId,
                      });
                      alert("Recording Started");
                      fetchDisputeDetails();
                    } catch (e) {
                      alert("Failed to start");
                    }
                  }}
                >
                  Start Recording
                </Button>
              );
            }
          })()}

          <span>Case ID: {renderValue(disputeDetails?.caseId)}</span>
        </div>
      </div>

      {/* MAIN */}
      <div style={{ display: "flex", flex: 1, position: "relative" }}>
        {/* Meeting area — parent must be position: relative for absolute children */}
        <div
          ref={meetingContainer}
          style={{
            width: isSidebarOpen ? "80vw" : "100vw",
            height: "calc(100vh - 100px)",
            backgroundColor: "#1C2E4A",
            position: "relative",
            transition: "width 0.3s ease-in-out", // Smooth width transition
          }}
        />

        {/* Sidebar */}
        <div
          style={{
            width: isSidebarOpen ? "20vw" : "0",
            height: "calc(100vh - 100px)",
            overflowY: isSidebarOpen ? "auto" : "hidden",
            padding: isSidebarOpen ? "1rem" : "0",
            backgroundColor: "#fff",
            color: "#000",
            boxShadow: isSidebarOpen ? "-2px 0 5px rgba(0,0,0,0.1)" : "none",
            position: "absolute",
            right: 0,
            top: 0,
            transform: isSidebarOpen ? "translateX(0)" : "translateX(100%)",
            transition:
              "transform 0.3s ease-in-out, width 0.3s ease-in-out, padding 0.3s ease-in-out", // Smooth slide animation
            zIndex: 1000,
          }}
        >
          {isSidebarOpen && (
            <>
              {loading ? (
                <div
                  className="d-flex justify-content-center align-items-center"
                  style={{ height: "100%" }}
                >
                  <Spinner animation="border" variant="primary" />
                  <span className="ms-2">Loading dispute details...</span>
                </div>
              ) : !disputeDetails ? (
                <div className="text-center mt-5">
                  <h5>No Dispute Data Available</h5>
                </div>
              ) : (
                <>
                  {/* Claimant */}
                  <Card className="mb-2">
                    <Card.Body className="p-2">
                      <h6 className="fw-bold mb-2 text-primary">
                        Claimant Information
                      </h6>
                      <p className="mb-1">
                        <strong>Name:</strong>{" "}
                        {renderValue(disputeDetails.claimantName)}
                      </p>
                      <p className="mb-1">
                        <strong>Address:</strong>{" "}
                        {renderValue(disputeDetails.address)}
                      </p>
                      <p className="mb-1">
                        <strong>City:</strong>{" "}
                        {renderValue(disputeDetails.city)}
                      </p>
                      <p className="mb-1">
                        <strong>Zip Code:</strong>{" "}
                        {renderValue(disputeDetails.zipCode)}
                      </p>
                      <p className="mb-1">
                        <strong>Country:</strong>{" "}
                        {renderValue(disputeDetails.country)}
                      </p>
                      <p className="mb-1">
                        <strong>Phone:</strong>{" "}
                        {renderValue(disputeDetails.phone)}
                      </p>
                      <p className="mb-1">
                        <strong>Email:</strong>{" "}
                        {renderValue(disputeDetails.email)}
                      </p>
                    </Card.Body>
                  </Card>

                  {/* Business */}
                  <Card className="mb-2">
                    <Card.Body className="p-2">
                      <h6 className="fw-bold mb-2 text-primary">
                        Business Details
                      </h6>
                      <p className="mb-1">
                        <strong>Nature of Business:</strong>{" "}
                        {renderValue(disputeDetails.natureOfBusiness)}
                      </p>
                      <p className="mb-1">
                        <strong>Industry:</strong>{" "}
                        {renderValue(disputeDetails.industry)}
                      </p>
                      <p className="mb-1">
                        <strong>AECCI Registration No:</strong>{" "}
                        {renderValue(disputeDetails.aecciRegNo)}
                      </p>
                      <p className="mb-1">
                        <strong>Member Type:</strong>{" "}
                        {renderValue(disputeDetails.memberType)}
                      </p>
                    </Card.Body>
                  </Card>

                  {/* Respondent */}
                  <Card className="mb-2">
                    <Card.Body className="p-2">
                      <h6 className="fw-bold mb-2 text-primary">
                        Respondent Information
                      </h6>
                      <p className="mb-1">
                        <strong>Company Name:</strong>{" "}
                        {renderValue(disputeDetails.respondentCompanyName)}
                      </p>
                      <p className="mb-1">
                        <strong>Name:</strong>{" "}
                        {renderValue(
                          disputeDetails.respondentRepresentativeName
                        )}
                      </p>
                      <p className="mb-1">
                        <strong>Email:</strong>{" "}
                        {renderValue(disputeDetails.respondentEmail)}
                      </p>
                      <p className="mb-1">
                        <strong>Address:</strong>{" "}
                        {renderValue(disputeDetails.respondentAddress)}
                      </p>
                      <p className="mb-1">
                        <strong>State:</strong>{" "}
                        {renderValue(disputeDetails.respondentState)}
                      </p>
                      <p className="mb-1">
                        <strong>Country:</strong>{" "}
                        {renderValue(disputeDetails.respondentCountry)}
                      </p>
                    </Card.Body>
                  </Card>

                  {/* Dispute */}
                  <Card className="mb-2">
                    <Card.Body className="p-2">
                      <h6 className="fw-bold mb-2 text-primary">
                        Dispute Details
                      </h6>
                      <p className="mb-1">
                        <strong>Application No:</strong>{" "}
                        {renderValue(disputeDetails.appNo)}
                      </p>
                      <p className="mb-1">
                        <strong>Place:</strong>{" "}
                        {renderValue(disputeDetails.place)}
                      </p>
                      <p className="mb-1">
                        <strong>Product:</strong>{" "}
                        {renderValue(disputeDetails.product)}
                      </p>
                      <p className="mb-1">
                        <strong>Application Date:</strong>{" "}
                        {formatDate(disputeDetails.createdAt)}
                      </p>
                      <p className="mb-1">
                        <strong>Date:</strong> {formatDate(disputeDetails.date)}
                      </p>
                      <p className="mb-1">
                        <strong>Agreement Start:</strong>{" "}
                        {formatDate(disputeDetails.agreementStartDate)}
                      </p>
                      <p className="mb-1">
                        <strong>Dispute Start:</strong>{" "}
                        {formatDate(disputeDetails.disputeStartDate)}
                      </p>
                    </Card.Body>
                  </Card>

                  {/* Financial */}
                  <Card className="mb-2">
                    <Card.Body className="p-2">
                      <h6 className="fw-bold mb-2 text-primary">
                        Financial Details
                      </h6>
                      <p className="mb-1">
                        <strong>Invoice Amount:</strong>{" "}
                        {renderCurrency(disputeDetails.commercialInvoiceAmount)}
                      </p>
                      <p className="mb-1">
                        <strong>Amount of Activity:</strong>{" "}
                        {renderCurrency(disputeDetails.amountOfActivity)}
                      </p>
                      <p className="mb-1">
                        <strong>Quantum of Claim:</strong>{" "}
                        {renderCurrency(disputeDetails.quantumOfClaim)}
                      </p>
                      <p className="mb-1">
                        <strong>Amount in Dispute:</strong>{" "}
                        {renderCurrency(disputeDetails.amountInDispute)}
                      </p>
                    </Card.Body>
                  </Card>

                  {/* Description */}
                  <Card className="mb-2">
                    <Card.Body className="p-2">
                      <h6 className="fw-bold mb-2 text-primary">
                        Dispute Description
                      </h6>
                      <p className="mb-1">
                        {renderValue(disputeDetails.disputeDescription)}
                      </p>
                    </Card.Body>
                  </Card>

                  {/* Uploaded Documents */}
                  <Card className="mb-2">
                    <Card.Body className="p-2">
                      <h6 className="fw-bold mb-2 text-primary">
                        Uploaded Documents
                      </h6>
                      {getDocuments(disputeDetails).length > 0 ? (
                        <Table bordered size="sm" responsive className="mb-0">
                          <thead>
                            <tr>
                              <th>Document Type</th>
                              <th>Action</th>
                            </tr>
                          </thead>
                          <tbody>
                            {getDocuments(disputeDetails).map((doc, index) => (
                              <tr key={index}>
                                <td className="py-1">{doc.documentType}</td>
                                <td className="py-1">
                                  <Button
                                    variant="primary"
                                    size="sm"
                                    className="py-0 px-2"
                                    style={{ fontSize: "12px" }}
                                    onClick={() => handleViewDocument(doc)}
                                    disabled={!doc.url}
                                  >
                                    <FaFileAlt className="me-1" /> View
                                  </Button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </Table>
                      ) : (
                        <p className="mb-0">No documents available</p>
                      )}
                    </Card.Body>
                  </Card>

                  {/* Time Table Documents */}
                  <Card className="mb-2">
                    <Card.Body className="p-2">
                      <h6 className="fw-bold mb-2 text-primary">
                        Time Table Documents
                      </h6>
                      {getTimeTableDocuments(disputeDetails).length > 0 ? (
                        <Table bordered size="sm" responsive className="mb-0">
                          <thead>
                            <tr>
                              <th>Document Type</th>
                              <th>Action</th>
                            </tr>
                          </thead>
                          <tbody>
                            {getTimeTableDocuments(disputeDetails).map(
                              (doc, index) => (
                                <tr key={index}>
                                  <td className="py-1">{doc.documentType}</td>
                                  <td className="py-1">
                                    <Button
                                      variant="primary"
                                      size="sm"
                                      className="py-0 px-2"
                                      style={{ fontSize: "12px" }}
                                      onClick={() => handleViewDocument(doc)}
                                      disabled={!doc.url}
                                    >
                                      <FaFileAlt className="me-1" /> View
                                    </Button>
                                  </td>
                                </tr>
                              )
                            )}
                          </tbody>
                        </Table>
                      ) : (
                        <p className="mb-0">
                          No time table documents available
                        </p>
                      )}
                    </Card.Body>
                  </Card>

                  {/* Registered Respondent */}
                  {disputeDetails.respondentId && (
                    <Card className="mb-2">
                      <Card.Body className="p-2">
                        <h6 className="fw-bold mb-2 text-primary">
                          Registered Respondent Details
                        </h6>
                        <p className="mb-1">
                          <strong>Company:</strong>{" "}
                          {renderValue(disputeDetails.respondentId.companyName)}
                        </p>
                        <p className="mb-1">
                          <strong>Name:</strong>{" "}
                          {renderValue(
                            `${disputeDetails.respondentId.firstName} ${disputeDetails.respondentId.surName}`
                          )}
                        </p>
                        <p className="mb-1">
                          <strong>Email:</strong>{" "}
                          {renderValue(disputeDetails.respondentId.email)}
                        </p>
                      </Card.Body>
                    </Card>
                  )}

                  {/* Appointed Arbitrators */}
                  {disputeDetails.appointedArbitratorIds?.length > 0 && (
                    <Card className="mb-2">
                      <Card.Body className="p-2">
                        <h6 className="fw-bold mb-2 text-primary">
                          Appointed Arbitrators
                        </h6>
                        <Table bordered size="sm" responsive className="mb-0">
                          <thead>
                            <tr>
                              <th>Name</th>
                            </tr>
                          </thead>
                          <tbody>
                            {disputeDetails.appointedArbitratorIds.map(
                              (arb, i) => (
                                <tr key={i}>
                                  <td className="py-1">
                                    {renderValue(
                                      `${arb.firstName} ${arb.surName}`
                                    )}
                                  </td>
                                </tr>
                              )
                            )}
                          </tbody>
                        </Table>
                      </Card.Body>
                    </Card>
                  )}

                  {/* Claimant Other Parties */}
                  {disputeDetails.claimantOtherPartyIds?.length > 0 && (
                    <Card className="mb-2">
                      <Card.Body className="p-2">
                        <h6 className="fw-bold mb-2 text-primary">
                          Claimant Other Parties
                        </h6>
                        <Table bordered size="sm" responsive className="mb-0">
                          <thead>
                            <tr>
                              <th>Name</th>
                              <th>Role</th>
                              <th>doc</th>
                            </tr>
                          </thead>
                          <tbody>
                            {disputeDetails.claimantOtherPartyIds.map(
                              (party, i) => (
                                <tr key={i}>
                                  <td className="py-1">
                                    {renderValue(
                                      `${party.firstName} ${party.lastName}`
                                    )}
                                    {party.isRegistered ? (
                                      <Badge bg="success" className="ms-2">
                                        Registered
                                      </Badge>
                                    ) : (
                                      <Badge bg="secondary" className="ms-2">
                                        Non-Registered
                                      </Badge>
                                    )}
                                  </td>
                                  <td className="py-1">
                                    {renderValue(party.partyType)}
                                  </td>
                                  <td className="py-1">
                                    {party.claimantStatement ? (
                                      <Button
                                        variant="primary"
                                        size="sm"
                                        className="py-0 px-2"
                                        style={{ fontSize: "12px" }}
                                        onClick={() =>
                                          handleViewDocument({
                                            documentType: "Claimant Statement",
                                            url: party.claimantStatement,
                                          })
                                        }
                                      >
                                        <FaFileAlt className="me-1" /> View
                                      </Button>
                                    ) : (
                                      "None"
                                    )}
                                  </td>
                                </tr>
                              )
                            )}
                          </tbody>
                        </Table>
                      </Card.Body>
                    </Card>
                  )}

                  {/* Respondent Other Parties */}
                  {disputeDetails.respondentOtherPartyIds?.length > 0 && (
                    <Card className="mb-2">
                      <Card.Body className="p-2">
                        <h6 className="fw-bold mb-2 text-primary">
                          Respondent Other Parties
                        </h6>
                        <Table bordered size="sm" responsive className="mb-0">
                          <thead>
                            <tr>
                              <th>Name</th>
                              <th>Role</th>
                              <th>doc</th>
                            </tr>
                          </thead>
                          <tbody>
                            {disputeDetails.respondentOtherPartyIds.map(
                              (party, i) => (
                                <tr key={i}>
                                  <td className="py-1">
                                    {renderValue(
                                      `${party.firstName} ${party.lastName}`
                                    )}
                                    {party.isRegistered ? (
                                      <Badge bg="success" className="ms-2">
                                        Registered
                                      </Badge>
                                    ) : (
                                      <Badge bg="secondary" className="ms-2">
                                        Non-Registered
                                      </Badge>
                                    )}
                                  </td>
                                  <td className="py-1">
                                    {renderValue(party.partyType)}
                                  </td>
                                  <td className="py-1">
                                    {party.claimantStatement ? (
                                      <Button
                                        variant="primary"
                                        size="sm"
                                        className="py-0 px-2"
                                        style={{ fontSize: "12px" }}
                                        onClick={() =>
                                          handleViewDocument({
                                            documentType: "Claimant Statement",
                                            url: party.claimantStatement,
                                          })
                                        }
                                      >
                                        <FaFileAlt className="me-1" /> View
                                      </Button>
                                    ) : (
                                      "None"
                                    )}
                                  </td>
                                </tr>
                              )
                            )}
                          </tbody>
                        </Table>
                      </Card.Body>
                    </Card>
                  )}

                  {/* Status */}
                  <Card className="mb-2">
                    <Card.Body className="p-2">
                      <h6 className="fw-bold mb-2 text-primary">Status</h6>
                      <p className="mb-1">
                        <strong>Status:</strong>{" "}
                        <Badge bg={getStatusVariant(disputeDetails.status)}>
                          {renderValue(disputeDetails.status)}
                        </Badge>
                      </p>
                      <p className="mb-1">
                        <strong>Approval:</strong>{" "}
                        {renderValue(disputeDetails.isApproved)}
                      </p>
                    </Card.Body>
                  </Card>
                </>
              )}
            </>
          )}
        </div>

        {/* Sidebar Toggle Button */}
        <Button
          variant="light"
          style={{
            position: "absolute",
            right: isSidebarOpen ? "20vw" : "0",
            top: "10px",
            zIndex: 1001,
            borderRadius: "50%",
            width: "40px",
            height: "40px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            boxShadow: "0 2px 5px rgba(0,0,0,0.2)",
            transition: "right 0.3s ease-in-out",
          }}
          onClick={toggleSidebar}
        >
          {isSidebarOpen ? "<" : ">"}
        </Button>
      </div>

      {/* FOOTER */}
      <div
        style={{
          height: "40px",
          backgroundColor: "#0D274A",
          color: "rgba(255,255,255,0.85)",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "0 20px",
          fontSize: "13px",
          borderTop: "2px solid #1E3A5F",
        }}
      >
        <span>
          Hearing conducted under AECCI–IAC Arbitration Rules (2023 Edition)
        </span>
        <span>All communications encrypted and recorded for record.</span>
      </div>

      {/* Document Modal */}
      <Modal
        show={showDocumentModal}
        onHide={handleCloseDocumentModal}
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title>Document Viewer</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedDocument ? (
            <div>
              <h6>{selectedDocument.documentType}</h6>

              {selectedDocument.url ? (
                <iframe
                  src={selectedDocument.url}
                  style={{ width: "100%", height: "500px", border: "none" }}
                  title={selectedDocument.documentType}
                />
              ) : (
                <p>Document not available</p>
              )}
            </div>
          ) : (
            <p>No document selected</p>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseDocumentModal}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Waiting Room Modal */}
      <Modal
        show={showWaitingRoomModal}
        onHide={() => setShowWaitingRoomModal(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Waiting Room Requests</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {reqLoading ? (
            <div className="text-center">
              <Spinner animation="border" />
            </div>
          ) : pendingRequests.length === 0 ? (
            <p className="text-center">No pending requests.</p>
          ) : (
            <Table striped bordered hover size="sm">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {pendingRequests.map((req, idx) => (
                  <tr key={idx}>
                    <td>{req.name}</td>
                    <td>{req.email}</td>
                    <td>
                      <Button
                        variant="success"
                        size="sm"
                        onClick={() => approveRequest(req.userId)}
                      >
                        Approve
                      </Button>{" "}
                      <Button
                        variant="danger"
                        size="sm"
                        onClick={() => rejectRequest(req.userId)}
                      >
                        Reject
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default Room;
